import React, { createContext, useContext, useState, useEffect } from 'react';
import {
    signInWithEmailAndPassword,
    createUserWithEmailAndPassword,
    signInWithPopup,
    signOut,
    onAuthStateChanged,
    updateProfile,
    sendPasswordResetEmail,
} from 'firebase/auth';
import { doc, setDoc, getDoc, updateDoc, serverTimestamp, collection, addDoc, query, orderBy, limit, getDocs, increment } from 'firebase/firestore';
import { auth, db, googleProvider } from './firebaseConfig';

const AuthContext = createContext(null);

const DEFAULT_STARTER_CREDITS = 5;

// ── Firestore helpers — all wrapped in try/catch so offline = graceful ────
async function createUserDoc(firebaseUser, extra = {}) {
    try {
        const ref = doc(db, 'users', firebaseUser.uid);
        const snap = await getDoc(ref);
        if (!snap.exists()) {
            await setDoc(ref, {
                uid: firebaseUser.uid,
                name: firebaseUser.displayName || extra.name || 'User',
                email: firebaseUser.email,
                credits: DEFAULT_STARTER_CREDITS,
                total_generations: 0,
                role: 'user',
                referral_code: generateReferralCode(firebaseUser.uid, firebaseUser.email),
                referred_by_email: extra.referral_code || null,
                created_at: serverTimestamp(),
                ...extra,
            });
        }
        return (await getDoc(ref)).data();
    } catch (err) {
        console.warn('Firestore createUserDoc failed (offline or not enabled):', err.message);
        // Return basic profile from Auth data so app still works
        return buildFallbackProfile(firebaseUser, extra);
    }
}

async function getUserDoc(uid) {
    const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Firestore timeout (likely blocked by adblocker)')), 4000)
    );
    try {
        const ref = doc(db, 'users', uid);
        const snap = await Promise.race([getDoc(ref), timeout]);
        return snap.exists() ? snap.data() : null;
    } catch (err) {
        console.warn('Firestore getUserDoc failed (offline or not enabled):', err.message);
        return null;
    }
}

async function updateUserDoc(uid, updates) {
    // Hard timeout — adblockers block firestore.googleapis.com silently,
    // causing updateDoc to hang forever instead of throwing.
    const timeout = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Firestore timeout (likely blocked by adblocker)')), 4000)
    );
    try {
        const ref = doc(db, 'users', uid);
        await Promise.race([updateDoc(ref, updates), timeout]);
        return await Promise.race([getDoc(ref).then(s => s.data()), timeout]);
    } catch (err) {
        console.warn('Firestore updateUserDoc failed (offline or not enabled):', err.message);
        return null;
    }
}

function generateReferralCode(uid, email) {
    const prefix = String(email || '')
        .slice(0, 4)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, '') || 'USER';
    const suffix = String(uid).slice(-6).toUpperCase();
    return `REF${prefix}${suffix}`;
}

// Fallback profile used when Firestore is blocked (adblocker) or unreachable.
// IMPORTANT: do NOT set credits to a default value here — that would briefly
// show "5 credits" on every refresh until the SQLite sync completes, which is
// confusing and looks like the credit decrement was reverted.
// Leave credits as undefined so the UI can show "—" until syncCreditsOnLoad fills it in.
function buildFallbackProfile(firebaseUser, extra = {}) {
    return {
        uid: firebaseUser.uid,
        name: firebaseUser.displayName || extra.name || 'User',
        email: firebaseUser.email,
        credits: undefined, // ← will be hydrated from SQLite within ~500ms
        total_generations: 0,
        role: 'user',
        referral_code: generateReferralCode(firebaseUser.uid, firebaseUser.email),
    };
}

// ── Provider ─────────────────────────────────────────────────
export function AuthProvider({ children }) {
    const [firebaseUser, setFirebaseUser] = useState(undefined); // undefined = still loading
    const [userProfile, setUserProfile] = useState(null);       // Firestore profile (may be null)

    // Accounts created in the admin panel live only in SQLite — they have no
    // Firebase account, so signInWithEmailAndPassword can never authenticate
    // them and the operator is locked out of their own booth. Those sign in
    // against /api/auth/login instead; the backend's requireAuth already accepts
    // that session token as a Bearer, so every existing API call keeps working.
    const [localSession, setLocalSession] = useState(() => {
        try {
            const raw = localStorage.getItem('pb_local_session');
            return raw ? JSON.parse(raw) : null;
        } catch { return null; }
    });

    const persistLocalSession = (session) => {
        setLocalSession(session);
        try {
            if (session) localStorage.setItem('pb_local_session', JSON.stringify(session));
            else localStorage.removeItem('pb_local_session');
        } catch { /* private mode — session just won't survive a reload */ }
    };

    // Merge firebase auth + firestore profile into one user object
    const user = firebaseUser
        ? {
            // Firebase Auth fields
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            displayName: firebaseUser.displayName,
            photoURL: firebaseUser.photoURL,
            emailVerified: firebaseUser.emailVerified,
            // Firestore profile fields (with safe defaults)
            name: userProfile?.name || firebaseUser.displayName || 'User',
            credits: userProfile?.credits ?? DEFAULT_STARTER_CREDITS,
            total_generations: userProfile?.total_generations ?? 0,
            role: userProfile?.role || 'user',
            referral_code: userProfile?.referral_code || generateReferralCode(firebaseUser.uid, firebaseUser.email),
            // Rest of Firestore profile
            ...(userProfile || {}),
        }
        : localSession
            ? {
                uid: `local:${localSession.user.id}`,
                email: localSession.user.email,
                displayName: localSession.user.name,
                emailVerified: true,
                isLocalAccount: true,
                name: localSession.user.name || 'User',
                credits: localSession.user.credits ?? 0,
                total_generations: localSession.user.total_generations ?? 0,
                role: localSession.user.role || 'user',
                referral_code: localSession.user.referral_code || '',
            }
            : null;

    const loading = firebaseUser === undefined;

    // ── Watch auth state ───────────────────────────────────────
    useEffect(() => {
        const unsub = onAuthStateChanged(auth, async (fbUser) => {
            setFirebaseUser(fbUser || null);
            if (fbUser) {
                // Race: Firestore profile + SQLite credits. SQLite is the
                // authoritative source for credits — wait briefly for it before
                // showing anything, so the user doesn't see "5 credits" flicker.
                try {
                    await syncCreditsOnLoad(fbUser);
                } catch { /* fall through to Firestore */ }
                // Best-effort: load Firestore profile too (other metadata)
                getUserDoc(fbUser.uid).then((profile) => {
                    if (!profile) return;
                    setUserProfile((prev) => ({
                        ...(prev || {}),
                        ...profile,
                        // Don't let stale Firestore credits clobber our fresh SQLite value
                        credits: prev?.credits !== undefined ? prev.credits : profile.credits,
                    }));
                }).catch(() => {});
            } else {
                setUserProfile(null);
            }
        });
        return unsub;
    }, []);

    // Helper used in onAuthStateChanged (can't use refreshCreditsFromBackend
    // there because firebaseUser state isn't set yet)
    const syncCreditsOnLoad = async (fbUser) => {
        try {
            const token = await fbUser.getIdToken();
            const res = await fetch('/api/auth/me', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return;
            const data = await res.json();
            const backendUser = data?.user || data;
            if (!backendUser) return;
            const newCredits = Number(backendUser.credits ?? 0);
            try {
                await updateUserDoc(fbUser.uid, {
                    credits: newCredits,
                    referral_code: backendUser.referral_code,
                });
            } catch { /* offline ok */ }
            setUserProfile((prev) => ({
                ...(prev || buildFallbackProfile(fbUser)),
                credits: newCredits,
                referral_code: backendUser.referral_code,
                role: backendUser.role || 'user',
                total_generations: backendUser.total_generations ?? prev?.total_generations ?? 0,
            }));
        } catch { /* ignore */ }
    };

    // ── Auth actions ───────────────────────────────────────────
    const login = async ({ email, password }) => {
        try {
            const cred = await signInWithEmailAndPassword(auth, email, password);
            // Try Firestore, but don't fail auth if it's down
            const profile = await getUserDoc(cred.user.uid);
            setUserProfile(profile || buildFallbackProfile(cred.user));
            persistLocalSession(null);
            return cred.user;
        } catch (err) {
            // Only fall back when Firebase simply doesn't know this account.
            // A wrong password on a real Firebase account must stay a wrong
            // password, not silently try a second credential store.
            const unknownToFirebase = [
                'auth/user-not-found',
                'auth/invalid-credential',
                'auth/invalid-login-credentials',
                'auth/wrong-password',
            ].includes(err.code);
            if (!unknownToFirebase) throw err;

            const res = await fetch('/api/auth/login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok || !data?.token) throw err; // surface the original Firebase error

            persistLocalSession({ token: data.token, user: data.user });
            setUserProfile(null);
            return data.user;
        }
    };

    // Return the current Firebase ID token (or null if not signed in).
    // Exposed because callers often have access to userProfile (plain object)
    // but not the live FirebaseUser instance which has getIdToken().
    const getAuthToken = async () => {
        if (firebaseUser) {
            try { return await firebaseUser.getIdToken(); } catch { return null; }
        }
        return localSession?.token || null;
    };

    // Spend credits via the SQLite backend (single source of truth for credits).
    // Then mirror the new balance to Firestore so the UI is consistent.
    const spendCreditsBackend = async (amount, description = 'Image generation') => {
        if (!firebaseUser && !localSession) throw new Error('Not authenticated');
        // A negative amount is a refund after a failed generation. /api/credits/spend
        // rejects those outright ("Invalid amount"), so the credit was silently lost
        // every time generation failed — route refunds to the refund endpoint.
        const isRefund = amount < 0;
        const endpoint = isRefund ? '/api/credits/refund' : '/api/credits/spend';
        const value = Math.abs(amount);
        try {
            const token = await getAuthToken();
            if (!token) throw new Error('Not authenticated');
            const res = await fetch(endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ amount: value, description }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data?.error || `${isRefund ? 'refund' : 'spend'} failed (${res.status})`);
            const newCredits = data?.user?.credits ?? 0;
            // Mirror to Firestore + local state
            try {
                await updateUserDoc(firebaseUser.uid, { credits: newCredits });
            } catch { /* offline ok */ }
            setUserProfile((prev) => ({
                ...(prev || buildFallbackProfile(firebaseUser)),
                credits: newCredits,
            }));
            return newCredits;
        } catch (e) {
            console.warn('[spendCreditsBackend]', e.message);
            throw e;
        }
    };

    // Pull authoritative credits from SQLite (which handles payments + referral bonuses)
    // and mirror to Firestore so the UI shows the correct balance.
    // Call this after every purchase, and on app load.
    const refreshCreditsFromBackend = async () => {
        try {
            const token = await getAuthToken();
            if (!token) return null;
            const res = await fetch('/api/auth/me', {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return null;
            const data = await res.json();
            const backendUser = data?.user || data;
            if (!backendUser) return null;
            const newCredits = Number(backendUser.credits ?? 0);
            const newReferralCode = backendUser.referral_code;
            // Update Firestore + local state (best-effort, with timeout)
            try {
                await updateUserDoc(firebaseUser.uid, {
                    credits: newCredits,
                    referral_code: newReferralCode,
                    total_generations: backendUser.total_generations ?? 0,
                });
            } catch { /* offline — local state still updates */ }
            setUserProfile((prev) => ({
                ...(prev || buildFallbackProfile(firebaseUser)),
                credits: newCredits,
                referral_code: newReferralCode,
                total_generations: backendUser.total_generations ?? prev?.total_generations ?? 0,
            }));
            return { credits: newCredits, referral_code: newReferralCode };
        } catch (e) {
            console.warn('[refreshCreditsFromBackend]', e.message);
            return null;
        }
    };

    // Sync newly-authenticated Firebase user with local-api SQLite shadow,
    // passing the referral code so the bonus chain is wired up correctly.
    const syncWithBackend = async (firebaseUser, referral_code) => {
        try {
            const token = await getAuthToken();
            if (!token) throw new Error('Not authenticated');
            const res = await fetch('/api/auth/sync-firebase', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ referral_code: referral_code || null }),
            });
            if (!res.ok) {
                console.warn('[sync-firebase] non-ok:', res.status);
                return null;
            }
            const data = await res.json();
            // Mirror SQLite credits + referral_code into Firestore profile
            if (data.user) {
                await updateUserDoc(firebaseUser.uid, {
                    credits: data.user.credits,
                    referral_code: data.user.referral_code,
                    referred_by_email: referral_code || null,
                    synced_at: serverTimestamp(),
                });
            }
            return data.user;
        } catch (e) {
            console.warn('[sync-firebase] failed (offline?):', e.message);
            return null;
        }
    };

    const register = async ({ name, email, password, referral_code } = {}) => {
        const cred = await createUserWithEmailAndPassword(auth, email, password);
        await updateProfile(cred.user, { displayName: name }).catch(() => { });
        const profile = await createUserDoc(cred.user, { name, referral_code });
        setUserProfile(profile);
        // Sync to SQLite + wire up referral chain
        const synced = await syncWithBackend(cred.user, referral_code);
        if (synced) setUserProfile((prev) => ({ ...(prev || profile), ...synced }));
        return cred.user;
    };

    const loginWithGoogle = async () => {
        // Capture referral code from (in order of priority):
        //   1. sessionStorage — set by Login.jsx when user typed code manually
        //   2. URL params      — if user landed via shared link
        let referral_code = null;
        try {
            const stashed = sessionStorage.getItem('pending_referral_code');
            if (stashed) {
                referral_code = stashed;
                sessionStorage.removeItem('pending_referral_code');
            }
        } catch { /* sessionStorage unavailable */ }
        if (!referral_code) {
            const params = new URLSearchParams(window.location.search);
            referral_code = params.get('ref') || params.get('referral') || params.get('referral_code') || null;
        }

        const cred = await signInWithPopup(auth, googleProvider);
        const profile = await createUserDoc(cred.user, { referral_code });
        setUserProfile(profile || buildFallbackProfile(cred.user));
        const synced = await syncWithBackend(cred.user, referral_code);
        if (synced) setUserProfile((prev) => ({ ...(prev || profile), ...synced }));
        return cred.user;
    };

    const logout = async (redirectUrl) => {
        if (localSession) {
            fetch('/api/auth/logout', {
                method: 'POST',
                headers: { Authorization: `Bearer ${localSession.token}` },
            }).catch(() => { /* the local copy is cleared regardless */ });
        }
        persistLocalSession(null);
        await signOut(auth).catch(() => { /* not signed in with Firebase */ });
        setFirebaseUser(null);
        setUserProfile(null);
        window.location.href = redirectUrl || '/';
    };

    const updateMe = async (updates) => {
        // Local (SQLite-only) accounts have no Firestore document to mirror into.
        // SQLite is authoritative for credits anyway, so this is a no-op for them
        // rather than an error that would break the callers' happy path.
        if (localSession && !firebaseUser) return null;
        if (!firebaseUser) throw new Error('Not authenticated');
        const profile = await updateUserDoc(firebaseUser.uid, updates);
        if (profile) {
            setUserProfile(profile);
        } else {
            // Firestore unavailable — update local state only
            setUserProfile((prev) => ({ ...(prev || buildFallbackProfile(firebaseUser)), ...updates }));
        }
        return user;
    };

    // Save a generation to the SQLite backend via local-api.
    // We avoid Firestore here because adblockers commonly block it (~20% of users),
    // and SQLite is already running on our server with the right schema.
    const saveGeneration = async (data) => {
        try {
            const token = await getAuthToken();
            if (!token) return null;
            const res = await fetch('/api/generations', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    character_id: data.character_id ?? null,
                    character_name: data.character_name ?? null,
                    original_image_url: data.original_image ?? data.original_image_url ?? null,
                    generated_image_url: data.image_url ?? data.generated_image_url ?? null,
                    status: 'completed',
                    prompt_used: data.prompt_used ?? null,
                    quality: data.quality ?? null,
                    credits_spent: data.credits_spent ?? 1,
                    provider_used: data.provider ?? data.provider_used ?? null,
                }),
            });
            if (!res.ok) {
                console.warn('saveGeneration HTTP', res.status);
                return null;
            }
            const j = await res.json();
            // Bump local counter immediately
            setUserProfile((prev) => ({
                ...(prev || buildFallbackProfile(firebaseUser)),
                total_generations: (prev?.total_generations || 0) + 1,
            }));
            return j?.generation?.id ?? j?.id ?? true;
        } catch (err) {
            console.warn('saveGeneration failed:', err.message);
            return null;
        }
    };

    // Fetch the user's generations from SQLite backend.
    const fetchGenerations = async (max = 24) => {
        try {
            const token = await getAuthToken();
            if (!token) return [];
            const res = await fetch(`/api/generations?limit=${max}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!res.ok) return [];
            const j = await res.json();
            // Normalise to the shape the dashboard expects
            return (j.generations || []).map((g) => ({
                id: g.id,
                character_id: g.character_id,
                character_name: g.character_name,
                image_url: g.generated_image_url,
                original_image: g.original_image_url,
                quality: g.quality,
                provider: g.provider_used,
                createdAt: g.created_at,
            }));
        } catch (err) {
            console.warn('fetchGenerations failed:', err.message);
            return [];
        }
    };

    const resetPassword = (email) => sendPasswordResetEmail(auth, email);

    const me = () => user;

    return (
        <AuthContext.Provider
            value={{ user, loading, login, register, loginWithGoogle, logout, updateMe, resetPassword, me, saveGeneration, fetchGenerations, refreshCreditsFromBackend, spendCreditsBackend, getAuthToken }}
        >
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
}

export default AuthContext;
